/**
 * chat_translate.ts — LIVE CHAT TRANSLATION, the reader's side (2026-09-24). Server reducers in
 * server/src/reducers/chat_translate.ts; the worker in server/workers/chat-translate.
 *
 * THE TIERS, in order:
 *   1. ON-DEVICE — the browser's own Translator API (Chrome 138+; `Translator.availability/create/translate`). Only
 *      a pair already 'available' is used (a download needs a user gesture a chat line never carries). Private: the
 *      result never leaves this client, and it never writes the shared cache (no player may put words in another's
 *      mouth — only the worker writes that).
 *   2. THE SHARED CACHE — `chat_translation` rows the worker writes, one per (line, language): the first reader asks
 *      (`requestChatTranslation`), every reader of that language after gets it for free.
 * A line is taken to be in its SENDER's declared language (`chat_lang`) unless its script says otherwise
 * (chat_langs.lineLang); a line already in my language is never translated.
 *
 * Settings: localStorage `atlas:chat-translate` = 'auto' (default) | 'off'; `atlas:chat-lang` = a supported code
 * (default: the browser's language). Chat commands: /translate on|off|<lang>.
 */
import { atlas } from '../state.js';
import { CHAT_LANGS, chatLang, lineLang, normalizeLang, translatable } from './chat_langs.js';

export type TranslateEngine = 'on-device' | 'shared' | 'same';
export interface Translated { text: string; from: string; engine: TranslateEngine | string }

const LS_MODE = 'atlas:chat-translate';
const LS_LANG = 'atlas:chat-lang';
const lsGet = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string): void => { try { localStorage.setItem(k, v); } catch { /* private window */ } };

export function translateOn(): boolean { return lsGet(LS_MODE) !== 'off'; }
export function setTranslateOn(on: boolean): void { lsSet(LS_MODE, on ? 'auto' : 'off'); }
export function myChatLang(): string {
  const saved = lsGet(LS_LANG);
  if (saved && chatLang(saved)) return saved;
  const nav = typeof navigator !== 'undefined' ? (navigator.languages?.[0] ?? navigator.language ?? 'en') : 'en';
  return normalizeLang(nav);
}
export function setMyChatLang(code: string): boolean {
  if (!chatLang(code)) return false;
  lsSet(LS_LANG, code);
  return true;
}
export const langName = (code: string): string => chatLang(code)?.name ?? code;
export const supportedLangs = (): string[] => CHAT_LANGS.map((l) => l.code);

// ── the on-device tier ──────────────────────────────────────────────────────
interface OnDeviceTranslator { translate(text: string): Promise<string> }
interface TranslatorStatic {
  availability(o: { sourceLanguage: string; targetLanguage: string }): Promise<string>;
  create(o: { sourceLanguage: string; targetLanguage: string }): Promise<OnDeviceTranslator>;
}
const pairs = new Map<string, Promise<OnDeviceTranslator | null>>();
function translatorApi(): TranslatorStatic | null {
  const g = globalThis as unknown as { Translator?: TranslatorStatic };
  return g.Translator && typeof g.Translator.availability === 'function' ? g.Translator : null;
}
export function onDeviceSupported(): boolean { return translatorApi() !== null; }

async function onDevice(text: string, from: string, to: string): Promise<string | null> {
  const api = translatorApi();
  if (!api) return null;
  const key = `${from}>${to}`;
  let p = pairs.get(key);
  if (!p) {
    p = (async () => {
      try {
        const a = await api.availability({ sourceLanguage: from, targetLanguage: to });
        if (a !== 'available') return null; // 'downloadable' needs a user gesture; the shared cache covers it
        return await api.create({ sourceLanguage: from, targetLanguage: to });
      } catch { return null; }
    })();
    pairs.set(key, p);
  }
  const tr = await p;
  if (!tr) return null;
  try { return await tr.translate(text); } catch { return null; }
}

// ── the reader ──────────────────────────────────────────────────────────────
interface IdLike { toHexString(): string }
interface ChatLangRow { identity: IdLike; lang: string }
interface TranslationRow { key: string; msgId: bigint; lang: string; source: string; text: string; engine: string }

const waiting = new Map<string, Array<(t: Translated | null) => void>>();
const asked = new Set<string>();
let subscribedLang = '';
let wired = false;

function senderLang(senderHex: string): string {
  const conn = atlas.conn as unknown as { db: { chatLang?: { iter(): Iterable<ChatLangRow> } } } | null;
  try {
    for (const r of conn?.db.chatLang?.iter() ?? []) if (r.identity.toHexString() === senderHex) return r.lang;
  } catch { /* not subscribed */ }
  return 'en';
}

function cached(msgId: bigint, lang: string): TranslationRow | null {
  const conn = atlas.conn as unknown as { db: { chatTranslation?: { key: { find(k: string): TranslationRow | undefined } } } } | null;
  try { return conn?.db.chatTranslation?.key.find(`${msgId}:${lang}`) ?? null; } catch { return null; }
}

/** Wire the tables once a connection is up: my declared language, the others', and the cache in my language. */
export function wireChatTranslate(): void {
  const conn = atlas.conn as unknown as {
    db: { chatTranslation?: { onInsert(cb: (c: unknown, r: TranslationRow) => void): void; onUpdate?(cb: (c: unknown, o: TranslationRow, r: TranslationRow) => void): void } };
    reducers: { setChatLanguage(a: { lang: string }): Promise<void> };
    subscriptionBuilder(): { subscribe(q: string[]): unknown };
  } | null;
  if (!conn) return;
  const lang = myChatLang();
  if (!wired) {
    wired = true;
    const land = (r: TranslationRow): void => {
      const k = `${r.msgId}:${r.lang}`;
      const cbs = waiting.get(k);
      if (!cbs) return;
      waiting.delete(k);
      for (const cb of cbs) cb({ text: r.text, from: r.source, engine: r.engine === 'same' ? 'same' : 'shared' });
    };
    conn.db.chatTranslation?.onInsert((_c, r) => land(r));
    conn.db.chatTranslation?.onUpdate?.((_c, _o, r) => land(r));
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM chat_lang']); } catch { /* pre-publish */ }
  }
  if (subscribedLang !== lang) {
    subscribedLang = lang;
    try { conn.subscriptionBuilder().subscribe([`SELECT * FROM chat_translation WHERE lang = '${lang}'`]); } catch { /* pre-publish */ }
  }
  void conn.reducers.setChatLanguage({ lang }).catch(() => { /* an older server */ });
}

/**
 * A heard line → its translation into my language, or null when it needs none (mine, already my language, nothing
 * to translate, translation off) or none could be had. Resolves once; a shared-cache answer may take a few seconds.
 */
export async function translateLine(msgId: bigint, text: string, senderHex: string): Promise<Translated | null> {
  if (!translateOn() || !translatable(text)) return null;
  const to = myChatLang();
  const from = lineLang(text, senderLang(senderHex));
  if (from === to) return null;
  const local = await onDevice(text, from, to);
  if (local && local.trim() && local.trim() !== text.trim()) return { text: local.trim(), from, engine: 'on-device' };
  const hit = cached(msgId, to);
  if (hit) return hit.engine === 'same' ? null : { text: hit.text, from: hit.source || from, engine: 'shared' };
  const key = `${msgId}:${to}`;
  const conn = atlas.conn as unknown as { reducers: { requestChatTranslation(a: { msgId: bigint; lang: string }): Promise<void> } } | null;
  if (!conn) return null;
  return new Promise<Translated | null>((resolve) => {
    const list = waiting.get(key) ?? [];
    list.push((t) => resolve(t && t.engine !== 'same' ? t : null));
    waiting.set(key, list);
    if (!asked.has(key)) {
      asked.add(key);
      if (asked.size > 2000) asked.clear();
      void conn.reducers.requestChatTranslation({ msgId, lang: to }).catch(() => { waiting.delete(key); resolve(null); });
    }
    // Give up after 45 s (a worker that is down leaves the line as written).
    window.setTimeout(() => { if (waiting.get(key)) { waiting.delete(key); resolve(null); } }, 45_000);
  });
}

/** Probe lever state. */
export function chatTranslateState(): Record<string, unknown> {
  return { on: translateOn(), lang: myChatLang(), onDevice: onDeviceSupported(), waiting: waiting.size, asked: asked.size, subscribedLang };
}
